import { MessageHandlerService } from './../../core/utility/message-handler.service';
import { Component, OnInit, ChangeDetectorRef, AfterViewChecked, ViewEncapsulation, ViewChild, Input } from '@angular/core';
import { GridDataResult, DataStateChangeEvent, CellClickEvent, GridComponent } from '@progress/kendo-angular-grid';
import { CompositeFilterDescriptor } from '@progress/kendo-data-query';
import { AppConfigService } from 'src/app/core/service/app-config.service';
import { State } from '@progress/kendo-data-query';
import { SearchHelperService } from 'src/app/core/utility/search-helper.service';
import { ClinicalMessageListService } from 'src/app/core/service/grid-data/grid-data-clinical-message.service';
import { ClinicalMessageService } from 'src/app/core/service/clinical-message.service';
import { SettingService } from 'src/app/core/service/setting.service';
import { IntlService } from '@progress/kendo-angular-intl';

@Component({
  selector: 'app-clinical-message-list',
  encapsulation: ViewEncapsulation.None,
  templateUrl: './clinical-message-list.component.html',
  styleUrls: ['./clinical-message-list.component.scss']
})
export class ClinicalMessageListComponent implements OnInit, AfterViewChecked
{
  @Input() public dashboard: boolean;

  @ViewChild(GridComponent) public grid: GridComponent;

  /**
   * Clinical Message list Kendo grid state.
   */
  public state: State = {
    skip: 0,
    // Initial filter descriptor
    filter: {
      logic: 'and',
      filters: []
    }
  };

  public pageSizes = [5, 10, 25, 50, 100];
  public gridView: GridDataResult;
  public gridData: any = {};
  public count: number;
  public selectedMessageId: number;
  public reprocessDialogOpened = false;
  public enableAdvanced = false;
  public enableAutoExpand = false;
  public breadcrumb = '';
  public stringLiteralBreadcrumb = false;
  public selectedClinicalMessageKeys = [];
  public filterText = '';
  public patientLabel = {};

  constructor(
    private clinicalMessageListService: ClinicalMessageListService,
    private clinicalMessageService: ClinicalMessageService,
    private changeDetectorRef: ChangeDetectorRef,
    private appConfigService: AppConfigService,
    private searchHelperService: SearchHelperService,
    private messageHandlerService: MessageHandlerService,
    private settingService: SettingService,
    public intl: IntlService)
  {
    this.state.take = this.appConfigService.getConfig().pageSize;
  }

  /**
   * Provide loading property from Service.
   */
  public get loading(): boolean
  {
    return this.clinicalMessageListService.loading;
  }

  public ngOnInit(): void
  {
    this.settingService.getCachedSessionSettings().subscribe({
      next: (sessionSetting) =>
      {
        this.patientLabel = { patientLabel: sessionSetting.patientLabel };
      }
    });

    this.clinicalMessageListService.subscribe({
      next: (value) =>
      {
        if (value !== null)
        {
          value.data.forEach(d =>
          {
            d.createdOn = new Date(d.createdOn);
            if (d.lastModifiedOn !== null)
            {
              d.lastModifiedOn = new Date(d.lastModifiedOn);
            }
          });
        }

        // TODO - this interaction between the component and the data service can be simplified / clean up.
        this.gridView = {
          data: (value) ? this.gridData = value.data : this.gridData = [],
          total: (value) ? this.count = value.total : this.count = 0
        };
      }
    });
    this.getClinicalMessages();
  }

  public ngAfterViewChecked(): void
  {
    // explicit change detection to avoid "expression-has-changed-after-it-was-checked-error"
    this.changeDetectorRef.detectChanges();
  }

  /**
   * Data change event handler.
   *
   * @param state State of grid operations
   */
  public dataStateChange(state: DataStateChangeEvent): void
  {
    this.unselectClinicalMessage();
    this.state = state;
    this.clinicalMessageListService.queryByState(state);
  }

  /**
   * Filtering by search field
   */
  public filterChange(filter: CompositeFilterDescriptor): void
  {
    this.state.filter = filter;
    this.clinicalMessageListService.queryByState(this.state);
  }

  public searchFieldChange(filterText: string): void
  {
    this.state.skip = 0;
    const root = this.searchHelperService.fieldChange(filterText, this.state);
    this.filterText = filterText;
    this.filterChange(root);
  }

  public counter(i: number): any
  {
    return new Array(i);
  }

  /**
   * Event fires when a task is selected.
   */
  public onCellClick = (event: CellClickEvent): void =>
  {
    this.collapseAllRows();
    if (this.enableAutoExpand)
    {
      event.sender.expandRow(event.rowIndex);
    }
  };

  /**
   * Event fires when attempting to reprocess a message.
   *
   * @param messageId Current messageId.
   */
  public reprocessConfirmation(messageId: number): void
  {
    this.selectedMessageId = messageId;
    this.reprocessDialogOpened = true;
  }

  /**
   * Perform reprocessing of the message after a confirmation.
   */
  public reprocessMessage(messageId: number): void
  {
    this.clinicalMessageService.reprocessMessage(messageId).subscribe({
      next: () =>
      {
        this.getClinicalMessages();
      },
      error: (error) =>
      {
        this.messageHandlerService.displayError(error);
      },
      complete: () =>
      {
        this.reprocessDialogOpened = false;
      }
    });
  }

  /**
   * Indicates if the grid details visibility. Details will show only if the message have been processed.
   *
   * @param dataItem Grid dataItem
   * @param index Index of the data in the grid list.
   */
  public showDetails(dataItem: any): boolean
  {
    return dataItem.lastModifiedOn !== null;
  }

  /**
   * Get cases from API per the applied filter.
   *
   * @param filter Task filter string.
   */
  private getClinicalMessages(): void
  {
    this.unselectClinicalMessage();
    this.clinicalMessageListService.queryByState(this.state);
  }

  /**
   * Unselect any previous selected rows.
   */
  private unselectClinicalMessage(): void
  {
    this.selectedClinicalMessageKeys = [];
    this.collapseAllRows();
  }

  /**
   * Collapse all task details opened.
   */
  private collapseAllRows()
  {
    if (this.grid === undefined)
    {
      return;
    }
    // Will collapse all preceeding task details based on the page size and current page.
    const maxLength = this.appConfigService.getConfig().pageSize + this.state.skip;
    for (let i = 0; i < maxLength; i++)
    {
      this.grid.collapseRow(i);
    }
  }
}
